import { Link } from "react-router-dom";
import { Phone, ArrowRight, ShieldCheck, Hammer, Award, MapPin, Star } from "lucide-react";
import SeoHead from "../components/SeoHead";
import CTABand from "../components/CTABand";
import ContactForm from "../components/ContactForm";
import ReviewsSection from "../components/ReviewsSection";
import { SITE, SERVICES, ZONES, REVIEWS } from "../lib/site";
import { ARTICLES } from "../lib/articles";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/05cb100a-63bb-4d09-b28c-36b1f8545960/images/226e77906963d720c7257e47378a51d51ef9c757f55324e62f27f182e9f19f0c.png";
const STONE_IMG =
  "https://images.unsplash.com/photo-1690818676619-c55d2cf9c3b6?auto=format&fit=crop&w=1400&q=80";
// Visuels Bordeaux : Place de la Bourse / Miroir d'eau / Pont de Pierre / échoppe bordelaise
// Sources Wikimedia Commons (libres de droits, stables, CDN upload.wikimedia.org)
const BDX_PLACE_BOURSE =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Place_de_la_Bourse_et_le_miroir_d%27eau_-_Bordeaux_02.jpg/1280px-Place_de_la_Bourse_et_le_miroir_d%27eau_-_Bordeaux_02.jpg";
const BDX_PONT_PIERRE =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b4/151_-_Le_Pont_de_Pierre_-_Bordeaux.jpg/1280px-151_-_Le_Pont_de_Pierre_-_Bordeaux.jpg";
const BDX_MIROIR =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d3/Miroir_d%27eau_Bordeaux_3.jpg/1280px-Miroir_d%27eau_Bordeaux_3.jpg";
const BDX_ECHOPPE =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/7/77/%C3%89choppe_bordelaise.jpg/1280px-%C3%89choppe_bordelaise.jpg";

export default function Home() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": SITE.url + "/#localbusiness",
    name: SITE.legalName,
    alternateName: SITE.name,
    url: SITE.url,
    image: HERO_IMG,
    logo: HERO_IMG,
    telephone: SITE.phoneRaw,
    email: SITE.email,
    priceRange: "€€",
    description:
      "Cabinet expert en traitement des termites, du bois et de la mérule à Bordeaux et en Gironde. Expertise obligatoire, injection, pièges-appâts, garantie 5 ans.",
    sameAs: [SITE.parentSite, SITE.facebook],
    address: {
      "@type": "PostalAddress",
      streetAddress: SITE.address,
      postalCode: SITE.postalCode,
      addressLocality: SITE.city,
      addressRegion: SITE.region,
      addressCountry: "FR",
    },
    geo: { "@type": "GeoCoordinates", latitude: SITE.geo.lat, longitude: SITE.geo.lng },
    areaServed: ZONES.map((z) => z.name),
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        opens: "08:00",
        closes: "19:00",
      },
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: SITE.aggregateRating.value,
      reviewCount: SITE.aggregateRating.count,
      bestRating: 5,
      worstRating: 1,
    },
    review: REVIEWS.slice(0, 4).map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.name },
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
      reviewBody: r.text,
    })),
    makesOffer: SERVICES.map((s) => ({
      "@type": "Offer",
      itemOffered: { "@type": "Service", name: s.title, description: s.short },
    })),
  };

  return (
    <div data-testid="home-page">
      <SeoHead
        title="Termite Bordeaux — Traitement Termites & Insectes Xylophages | Devis 48 h"
        description="Termite Bordeaux : agence locale spécialisée traitement termites, charpente, capricornes, vrillettes, mérule et humidité à Bordeaux et en Gironde. Garantie 5 ans. ☎ 07 45 71 15 10"
        canonical={SITE.url + "/"}
        image={HERO_IMG}
        schema={schema}
        keywords={SITE.seoKeywords}
        breadcrumbs={[{ name: "Accueil", url: SITE.url + "/" }]}
      />

      {/* ÉVÉNEMENT À VENIR — Salon Médoc Immo 2027 */}
      <section className="bg-[#7A2E39] text-white" data-testid="home-event-banner">
        <Link
          to="/salon-medoc-immo-2027"
          className="block container-x py-3.5 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-sm hover:bg-[#8d3745] transition-colors"
          data-testid="home-event-link"
        >
          <span className="inline-flex items-center gap-2 bg-white/15 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold">
            🏛️ Événement
          </span>
          <span>
            <strong>Termite Habitat 33 au Salon Médoc Immo 2027</strong> — samedi 20 mars 2027 au Domaine de Cordet, Arsac
          </span>
          <span className="inline-flex items-center gap-1 underline underline-offset-4 font-semibold">
            Toutes les infos →
          </span>
        </Link>
      </section>

      {/* HERO */}
      <section className="relative isolate" data-testid="hero">
        <img
          src={HERO_IMG}
          alt="Traitement termites Bordeaux Gironde - Termite Habitat 33"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/30" />
        <div className="relative container-x py-24 lg:py-40">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 px-3 py-1 text-xs tracking-widest uppercase text-white mb-6">
              <MapPin className="w-3.5 h-3.5" /> Bordeaux · Gironde · Bassin d'Arcachon
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white font-serif font-semibold leading-[1.05] mb-5">
              Termite Bordeaux — votre agence locale anti-termites
            </h1>
            <p className="text-lg text-white/90 mb-8 max-w-xl">
              Expertise parasitaire, traitement termites, charpente, capricornes, vrillettes,
              mérule et humidité. Devis sous 48 h en Bordeaux Métropole, garantie 5 ans.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href={`tel:${SITE.phoneRaw}`} className="btn-primary text-base px-7 py-4" data-testid="hero-cta-call">
                <Phone className="w-4 h-4" /> {SITE.phone}
              </a>
              <Link
                to="/devis"
                className="bg-white/10 backdrop-blur-md border border-white/30 hover:bg-white/20 text-white inline-flex items-center justify-center gap-2 px-7 py-4 rounded-sm text-base font-bold tracking-wide transition-colors"
                data-testid="hero-cta-expertise"
              >
                Devis gratuit en ligne <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-3 text-white/90 text-sm">
              <div className="flex gap-0.5 text-[#FFB100]">
                {[1, 2, 3, 4, 5].map((i) => <Star key={i} className="w-4 h-4 fill-[#FFB100]" />)}
              </div>
              <span><strong>5,0 / 5</strong> sur Google · {SITE.aggregateRating.count} avis</span>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-xl">
              {[
                { v: "5 ans", l: "Garantie" },
                { v: "48 h", l: "Intervention rapide" },
                { v: "50 villes", l: "Gironde couverte" },
              ].map((s) => (
                <div key={s.l} className="text-white/90">
                  <div className="font-serif text-2xl">{s.v}</div>
                  <div className="text-xs tracking-widest uppercase text-white/70">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SEO Lexical band - boosts on-page keyword density naturally */}
      <section className="bg-white border-b border-[#D5CABD]" data-testid="seo-lexical">
        <div className="container-x py-6 text-xs text-gray-500 text-center tracking-wide">
          <span className="font-bold text-gray-700">Termite Bordeaux — </span>
          traitement termites Bordeaux · expertise termites Gironde · anti-termites Bordeaux ·
          traitement charpente · capricornes · vrillettes · mérule · humidité ·
          injection termites · pièges-appâts · Bordeaux Métropole · Bassin d'Arcachon · Médoc · Libournais
        </div>
      </section>

      {/* BORDEAUX VISUALS */}
      <section className="container-x py-20" data-testid="bordeaux-visual">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="eyebrow mb-3">Le bâti bordelais</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold leading-[1.05] text-gray-900 mb-5">
              Bordeaux : pierre, chêne, humidité — un terrain unique.
            </h2>
            <p className="text-gray-700 mb-4">
              Des quais classés UNESCO aux échoppes de Caudéran, en passant par les hôtels particuliers
              des Chartrons et les villas du Bassin d'Arcachon : le bâti bordelais combine pierre
              calcaire poreuse, charpentes en chêne et pin maritime, planchers anciens et caves voûtées.
            </p>
            <p className="text-gray-700 mb-6">
              Un cocktail idéal pour les termites souterrains, les capricornes, les vrillettes et la
              mérule. Notre agence est née pour répondre à cette spécificité locale : une expertise
              de proximité, une expertise adaptée à chaque type de bâti et une réactivité sous 48 h sur
              toute la métropole.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/zones" className="btn-primary" data-testid="bdx-zones">
                Voir nos 50 villes d'intervention <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/insectes-xylophages" className="btn-ghost" data-testid="bdx-insects">
                Insectes xylophages
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <img
              src={BDX_PLACE_BOURSE}
              alt="Place de la Bourse à Bordeaux — bâtis classés UNESCO"
              className="w-full h-48 sm:h-56 object-cover rounded-sm"
              loading="lazy"
            />
            <img
              src={BDX_PONT_PIERRE}
              alt="Pont de Pierre Bordeaux — passage Garonne et bâti ancien"
              className="w-full h-48 sm:h-56 object-cover rounded-sm mt-6"
              loading="lazy"
            />
            <img
              src={BDX_MIROIR}
              alt="Miroir d'eau Bordeaux — quais de la Garonne, humidité urbaine"
              className="w-full h-48 sm:h-56 object-cover rounded-sm"
              loading="lazy"
            />
            <img
              src={BDX_ECHOPPE}
              alt="Échoppe bordelaise typique en pierre — bâti girondin"
              className="w-full h-48 sm:h-56 object-cover rounded-sm mt-6"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* VIDEO SHOWCASE — termites en action */}
      <section className="bg-[#1B1814] text-white" data-testid="home-videos">
        <div className="container-x py-20">
          <div className="grid lg:grid-cols-3 gap-10 items-start">
            <div className="lg:col-span-1">
              <div className="eyebrow mb-3 text-[#D5A66A]">Vidéos chantiers</div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold leading-[1.05] mb-5">
                Les termites filmés sur nos chantiers.
              </h2>
              <p className="text-white/80 mb-6">
                Images réelles tournées en Gironde par notre équipe : termites en action,
                cordonnets, pièges-appâts, charpentes attaquées, bûchage et traitement curatif.
              </p>
              <Link to="/videos" className="btn-primary" data-testid="home-videos-link">
                Voir toutes les vidéos <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
              <video
                src="https://customer-assets.emergentagent.com/job_wonderful-greider-8/artifacts/y6yr44q0_2026-04-03-114323336.mp4"
                muted
                autoPlay
                loop
                playsInline
                preload="metadata"
                className="w-full aspect-video object-cover bg-black rounded-sm"
                aria-label="Colonie de termites observée sur chantier en Gironde"
                data-testid="home-video-1"
              />
              <video
                src="https://customer-assets.emergentagent.com/job_wonderful-greider-8/artifacts/7n16wmvr_2026-04-07-150457022.mp4"
                muted
                autoPlay
                loop
                playsInline
                preload="metadata"
                className="w-full aspect-video object-cover bg-black rounded-sm"
                aria-label="Bois infesté — termites en galerie"
                data-testid="home-video-2"
              />
            </div>
          </div>
        </div>
      </section>

      {/* PHOTOS TEASER */}
      <section className="container-x py-20" data-testid="home-photos">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div className="max-w-2xl">
            <div className="eyebrow mb-3">Photos terrain</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold leading-[1.05] text-gray-900 mb-3">
              Essaimages, cordonnets, pièges actifs.
            </h2>
            <p className="text-gray-700">
              Photos prises chez nos clients girondins. Reconnaissez les signes avant que la
              colonie ne s'installe durablement dans votre habitat.
            </p>
          </div>
          <Link to="/photos" className="link-underline text-sm font-bold uppercase tracking-widest" data-testid="home-photos-link">
            Voir toute la photothèque →
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            "https://customer-assets.emergentagent.com/job_wonderful-greider-8/artifacts/1gibmqar_20250211_164903.jpg",
            "https://customer-assets.emergentagent.com/job_wonderful-greider-8/artifacts/hsnkibvl_20250211_180619.jpg",
            "https://customer-assets.emergentagent.com/job_wonderful-greider-8/artifacts/3li5ls8q_20251118_100246.jpg",
          ].map((src, i) => (
            <Link
              to="/photos"
              key={src}
              className="group block aspect-[4/3] overflow-hidden border border-[#D5CABD] bg-gray-100 rounded-sm"
              data-testid={`home-photo-${i}`}
            >
              <img
                src={src}
                alt="Photo réelle chantier termites — Gironde"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </Link>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="container-x py-20 lg:py-28" data-testid="services-overview">
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-1">
            <div className="eyebrow mb-3">Nos expertises</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold leading-[1.05] text-gray-900 mb-5">
              Six protocoles, une méthode : la précision artisanale.
            </h2>
            <p className="text-gray-700 mb-6">
              Chaque chantier en Gironde commence par une expertise exhaustive et se termine par un
              procès‑verbal de traitement opposable, exigible à la revente.
            </p>
            <Link to="/services" className="link-underline text-sm font-bold uppercase tracking-widest" data-testid="services-overview-link">
              Voir tous les services →
            </Link>
          </div>
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-5">
            {SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="group bg-white border border-[#D5CABD] hover:border-[#7A2E39] hover:shadow-md transition-all p-6 rounded-sm"
                data-testid={`service-card-${s.slug}`}
              >
                <div className="font-serif text-2xl text-gray-900 mb-2">{s.title}</div>
                <p className="text-sm text-gray-600 mb-4">{s.short}</p>
                <span className="text-[#7A2E39] text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1">
                  En savoir plus <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-[#E8DCC9]/50 border-y border-[#D5CABD]" data-testid="why-section">
        <div className="container-x py-20 grid md:grid-cols-2 gap-12 items-center">
          <img
            src={STONE_IMG}
            alt="Façade en pierre bordelaise traitée contre les termites"
            className="rounded-sm w-full h-[420px] object-cover"
            loading="lazy"
          />
          <div>
            <div className="eyebrow mb-3">Notre approche</div>
            <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-gray-900 mb-5">
              L'expertise locale fait toute la différence en Gironde.
            </h2>
            <p className="text-gray-700 mb-6">
              Bordeaux concentre des risques uniques : climat humide, bâti ancien en pierre,
              proximité de la Garonne. Notre équipe locale intervient depuis Parempuyre sur
              l'ensemble de la métropole, du Médoc au Libournais en passant par le Bassin
              d'Arcachon.
            </p>
            <div className="grid sm:grid-cols-2 gap-5">
              {[
                { icon: ShieldCheck, t: "Garantie 5 ans", d: "Retraitement gratuit en cas de nouvelle infestation." },
                { icon: Award, t: "Certification CTB-P+", d: "Produits homologués Anses." },
                { icon: Hammer, t: "Artisans formés", d: "Aucune sous-traitance, équipes internes." },
                { icon: MapPin, t: "Local Bordeaux", d: "Connaissance fine du bâti girondin." },
              ].map((b) => (
                <div key={b.t} className="flex gap-3">
                  <div className="w-10 h-10 grid place-items-center bg-[#7A2E39] text-white rounded-sm shrink-0">
                    <b.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{b.t}</div>
                    <div className="text-sm text-gray-600">{b.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <ReviewsSection compact max={6} />

      {/* ZONES */}
      <section className="container-x py-20" data-testid="zones-overview">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-4">
          <div>
            <div className="eyebrow mb-3">Zones d'intervention</div>
            <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-gray-900">
              Toute la Gironde, depuis Parempuyre.
            </h2>
          </div>
          <Link to="/zones" className="link-underline text-sm font-bold uppercase tracking-widest" data-testid="zones-link">
            Voir toutes les zones →
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {ZONES.map((z) => (
            <Link
              key={z.slug}
              to={`/zones/${z.slug}`}
              className="text-sm bg-white border border-[#D5CABD] hover:border-[#7A2E39] hover:bg-[#F9F8F6] px-4 py-3 rounded-sm transition-colors"
              data-testid={`zone-chip-${z.slug}`}
            >
              Termites {z.name}
            </Link>
          ))}
        </div>
      </section>

      {/* BLOG TEASER */}
      <section className="bg-white border-y border-[#D5CABD]" data-testid="blog-teaser">
        <div className="container-x py-20">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-4">
            <div>
              <div className="eyebrow mb-3">Journal d'expert</div>
              <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-gray-900">
                Comprendre les termites en Gironde.
              </h2>
            </div>
            <Link to="/blog" className="link-underline text-sm font-bold uppercase tracking-widest" data-testid="blog-link">
              Tous les articles →
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {ARTICLES.slice(0, 3).map((a) => (
              <Link
                key={a.slug}
                to={`/blog/${a.slug}`}
                className="group block bg-[#F9F8F6] border border-[#D5CABD] hover:border-[#7A2E39] transition-colors"
                data-testid={`blog-card-${a.slug}`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={a.cover} alt={a.title} loading="lazy"
                       className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5">
                  <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">
                    {a.readingMinutes} min · {new Date(a.date).toLocaleDateString("fr-FR")}
                  </div>
                  <h3 className="font-serif text-xl text-gray-900 leading-snug mb-2">{a.title}</h3>
                  <p className="text-sm text-gray-600 line-clamp-3">{a.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="container-x py-20 grid lg:grid-cols-2 gap-12" data-testid="home-contact">
        <div>
          <div className="eyebrow mb-3">Demande d'expertise</div>
          <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-gray-900 mb-5">
            Un avis d'expert sous 24 h.
          </h2>
          <p className="text-gray-700 mb-6">
            Remplissez ce formulaire : nous analysons votre situation et vous rappelons sous 24 h ouvrées.
            Pour un cas urgent, appelez directement le {SITE.phone}.
          </p>
          <div className="space-y-3 text-sm text-gray-700">
            <div className="flex gap-2"><Phone className="w-4 h-4 text-[#7A2E39]" /> {SITE.phone}</div>
            <div className="flex gap-2">
              <MapPin className="w-4 h-4 text-[#7A2E39] mt-0.5" />
              {SITE.address}, {SITE.postalCode} {SITE.city.toUpperCase()}
            </div>
          </div>
        </div>
        <ContactForm />
      </section>

      <CTABand />
    </div>
  );
}
